import {
	TableRow,
	TableColumn,
	TableHeader,
	TableBody,
	DataTable,
	FontIcon,
	ListItem,
	MenuButton,
	Button,
	SelectField,
} from 'react-md'
import { TableContainer } from 'ui/StyledComponents/Tables'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import Uploader from 'ui/components/Uploader'
import PDFPreview from 'ui/components/PDFPreview'
import { useSelectedFinanceCompany } from 'ui/ManagerFinance/hooks/useSelectedFinanceCompany'
import { useEffect, useMemo, useState } from 'react'
import { _makeSignedProxyUrl } from 'state/finance-viilutalous-actions'
import type { Attachment } from '../types'

const Container = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 300px;
`

const Content = styled.div`
	overflow: auto;
`

const StyledDataTable = styled(DataTable)`
	height: auto;
	max-height: calc(100vh - 400px);
	td {
		white-space: nowrap;
	}
`

// TODO: This should be moved to somewhere useful intead of being inline here
const useAttachmentUrls = (ids?: number[]) => {
	const [urls, setUrls] = useState<string[]>([])
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<string | null>(null)
	const { company } = useSelectedFinanceCompany()
	useEffect(() => {
		const doFetch = async () => {
			if (!ids?.length || !company) return
			try {
				setLoading(true)
				setError(null)
				const getUrl = async (id: number) => {
					const result = await _makeSignedProxyUrl({
						type: 'attachments',
						resourceId: id,
						companyUUID: company?.uuid || '',
						databaseSource:
							company?.integrations?.viiluTalous?.databaseSource || '',
					})
					if (!result?.ok) throw new Error('Failed to fetch')
					const fileProxyUrl = await result.text()
					return fileProxyUrl
				}
				const urls = await Promise.all(ids.map((id) => getUrl(id)))
				setUrls(urls)
			} catch (error) {
				console.error('Error fetching signed download URL:', error)
				setError('Failed to fetch attachment URL')
			} finally {
				setLoading(false)
			}
		}
		doFetch()
	}, [company, ids])
	return { urls, loading, error }
}

type AttachmentsProps = {
	attachments: Attachment[]
}

const Attachments = (props: AttachmentsProps) => {
	const { attachments } = props

	const intl = useIntl()

	//const { watch } = useFormContext<CreateFormInputs>()

	const attachmentIds = useMemo(() => {
		return attachments.map((attachment) => attachment.id)
	}, [attachments])

	const { urls } = useAttachmentUrls(attachmentIds)

	const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(0)

	const renderFile = (attachment: Attachment) => {
		const menu = (
			<MenuButton
				id="menu-button"
				icon
				menuItems={[
					<ListItem
						key={1}
						primaryText={intl.formatMessage({
							defaultMessage: 'Poista liite',
							description: 'Delete attachment',
						})}
						leftIcon={<FontIcon>delete</FontIcon>}
						//TODO: Onclick ask if u want to delete it
						onClick={() => {}}
						className="md-divider-border md-divider-border--right"
					/>,
				]}
				centered
				anchor={{
					x: MenuButton.HorizontalAnchors['CENTER'],
					y: MenuButton.VerticalAnchors['CENTER'],
				}}
			>
				more_vert
			</MenuButton>
		)

		//TODO: For description, possibility to edit or something
		return (
			<TableRow key={`${attachment.databaseSource}-${attachment.id}`}>
				<TableColumn key="filename">{attachment.name}</TableColumn>
				<TableColumn key="description">{attachment.description}</TableColumn>
				<TableColumn
					key="children"
					style={{ display: 'flex', justifyContent: 'end' }}
				>
					<div>{menu}</div>
				</TableColumn>
			</TableRow>
		)
	}

	const renderFilesTable = () => {
		return (
			<TableContainer>
				<StyledDataTable plain>
					<TableHeader
						style={{
							position: 'sticky',
							top: 0,
							zIndex: 1,
						}}
					>
						<TableRow>
							<TableColumn key="title">
								<p className="text-strong text-subtle">
									<FormattedMessage
										defaultMessage="Tiedostonimi"
										description="Filename"
									/>
								</p>
							</TableColumn>
							<TableColumn key="description">
								<p className="text-strong text-subtle">
									<FormattedMessage
										defaultMessage="Seloste"
										description="Description"
									/>
								</p>
							</TableColumn>
							<TableColumn key="children-header"></TableColumn>
						</TableRow>
					</TableHeader>

					<TableBody>
						{attachments.map((attachment) => {
							return renderFile(attachment)
						})}
					</TableBody>
				</StyledDataTable>
			</TableContainer>
		)
	}

	const renderAttachmentPreview = () => {
		const attachment = attachments[selectedAttachmentIndex]
		const url = urls[selectedAttachmentIndex]

		if (!attachment) {
			return null
		}

		const menuItems = attachments.map((att, index) => ({
			label: att.name,
			value: index,
		}))

		let previewComponent
		if (attachment.mime === 'application/pdf') {
			previewComponent = <PDFPreview fileURL={url} refreshing={false} />
		} else if (
			[
				'image/apng',
				'image/png',
				'image/jpeg',
				'image/gif',
				'image/webp',
				'image/bmp',
				'image/svg+xml',
				'image/vnd.microsoft.icon',
				'image/tiff',
			].includes(attachment.mime)
		) {
			previewComponent = (
				<img
					src={url}
					alt={attachment.name}
					style={{
						maxWidth: '100%',
						maxHeight: '80vh',
						objectFit: 'contain',
						marginTop: '1rem',
					}}
				/>
			)
		} else {
			previewComponent = (
				<div
					style={{
						width: '100%',
						paddingTop: '1rem',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						gap: '1rem',
					}}
				>
					{intl.formatMessage({
						defaultMessage:
							'Esikatselu ei ole saatavilla tälle tiedostotyypille.',
						description: 'Preview not available for this file type.',
					})}
					<Button
						flat
						iconEl={<FontIcon style={{ opacity: 0.5 }}>get_app</FontIcon>}
						onClick={() => {
							window.open(url, '_blank')
						}}
					>
						{intl.formatMessage({
							defaultMessage: 'Lataa tiedosto',
							description: 'Download file',
						})}
					</Button>
				</div>
			)
		}

		return (
			<div className="full-width full-height padding-top">
				<SelectField
					id="select-attachment-to-preview"
					label={intl.formatMessage({
						defaultMessage: 'Esikatseltava liite',
						description: 'Attachment to preview',
					})}
					value={selectedAttachmentIndex}
					menuItems={menuItems}
					onChange={(value) => setSelectedAttachmentIndex(value as number)}
					style={{ width: '100%', background: '#fafafa' }}
				/>
				{previewComponent}
			</div>
		)
	}

	const renderContent = () => {
		return (
			<Container>
				{attachments.length === 0 ? (
					<Uploader
						acceptedContentTypes={[
							'image/png',
							'image/jpeg',
							'image/jpg',
							'application/pdf',
						]}
						//onFilesChanged={onDrop}
						singularForm="Raahaa kuitti tai lasku tähän."
					/>
				) : (
					<Button
						iconChildren={<FontIcon>add</FontIcon>}
						style={{ textAlign: 'start' }}
					>
						<FormattedMessage
							defaultMessage="Lisää liiteitä"
							description="Add attachments"
						/>
					</Button>
				)}

				{renderFilesTable()}

				{renderAttachmentPreview()}
			</Container>
		)
	}

	return <Content>{renderContent()}</Content>
}

export default Attachments
